import { jsPDF } from 'jspdf';

// =============================================================================
// Assignment / Reflection submission PDF generator
// -----------------------------------------------------------------------------
// Builds a printable packet of what students actually turned in for a set of
// assignment-type items. Used by the Assignment Submissions grid so staff can
// pull a week of reflections (or any assignment) as a single PDF instead of
// opening every submission one at a time.
//
// Typed submissions are rendered inline. Image uploads (phone photos of a
// handwritten reflection, screenshots) are embedded at full width. Anything we
// cannot draw - Word docs, PDFs, HEIC - is listed with its download URL so the
// packet still tells the reader where the file lives.
//
// Public API:
//   generateSubmissionsPdf(entries, { reportTitle, generatedFor, fileName })
//     entries: [{ student, rows: [{ item, submission, files }] }]
// =============================================================================

const MARGIN = 18;
const FONT_BODY = 10;
const FONT_SMALL = 8.5;
const FONT_H1 = 18;
const FONT_H2 = 13;
const FONT_H3 = 11;

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

// Formats no browser canvas will decode. iPhone uploads land here regularly,
// so they get the same named-link treatment as a Word doc rather than stalling
// an <img> load that never fires onload or onerror.
const UNDECODABLE_TYPE = /^image\/(heic|heif|tiff?)$/;
const UNDECODABLE_EXT = /\.(heic|heif|tiff?)(\?|$)/i;

// Long-edge cap before embedding, sized against the A4 content width: 210mm
// less two 18mm margins is 174mm, about 6.85in, so 1400px lands near 200 DPI.
// Handwriting stays legible from roughly 150 DPI up, and 1400 is 23% fewer
// pixels to encode and embed than 1600. Quality 0.75 costs little on a photo
// of handwriting and takes another chunk off both encode time and file size.
const MAX_IMAGE_EDGE = 1400;
const JPEG_QUALITY = 0.75;

// One slow object should not hold the whole export.
const IMAGE_FETCH_TIMEOUT_MS = 15000;

// Enough parallelism to keep the network busy, few enough that decoding does
// not monopolise the main thread.
const IMAGE_CONCURRENCY = 4;

// Safety rails. A cohort-wide export stalled after all its fetches had
// finished, burning CPU for 8.5 minutes on one student with no error and no
// end. Neither guard is a diagnosis: they exist so that whatever the cause,
// the export stops with a message naming where it stopped instead of spinning
// until someone closes the tab.
//
// MAX_PAGES catches runaway placement - anything that keeps adding pages
// without making progress. A normal packet is a few pages per student.
// STUDENT_TIME_BUDGET_MS catches everything else: measured worst case was 174s
// for a 104-image student, so 5 minutes is generous for one student's drawing
// pass and still bounded.
const MAX_PAGES = 600;
const STUDENT_TIME_BUDGET_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
function fmtDateTime(ts) {
  if (!ts) return 'Not submitted';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return 'Not submitted';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtToday() {
  return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function statusLabel(submission) {
  if (!submission) return 'Not submitted';
  const map = {
    draft: 'Draft',
    submitted: 'Submitted',
    reviewed: 'Reviewed',
    graded: 'Graded',
    returned: 'Returned',
  };
  return map[submission.status] || 'Submitted';
}

function isImage(file) {
  const type = (file.file_type || '').toLowerCase();
  const name = (file.file_name || file.file_url || '').toLowerCase();

  // Check the unsupported list first: a .heic named "photo.heic" with a
  // mislabelled image/jpeg type must not reach the decoder.
  if (UNDECODABLE_TYPE.test(type) || UNDECODABLE_EXT.test(name)) return false;

  if (IMAGE_TYPES.includes(type)) return true;
  return /\.(jpe?g|png)(\?|$)/.test(name);
}

// File names are stored exactly as the browser reported them, which for some
// uploads means they arrive percent-encoded. Show the readable version.
function displayFileName(file) {
  const raw = file.file_name || 'Attachment';
  try {
    return decodeURIComponent(raw);
  } catch (_) {
    return raw;
  }
}

// ---------------------------------------------------------------------------
// Drawing primitives - everything funnels through addPageIfNeeded() so rows
// never get cut in half across a page break.
// ---------------------------------------------------------------------------
function makeContext(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  return {
    doc,
    pageWidth,
    pageHeight,
    contentWidth: pageWidth - MARGIN * 2,
    y: MARGIN,
    pages: 1,
    studentLabel: '',
    studentStartedAt: 0,
  };
}

// Every page break goes through here so the cap cannot be bypassed.
function addPage(ctx) {
  ctx.pages += 1;
  if (ctx.pages > MAX_PAGES) {
    throw new Error(
      `Export stopped after ${MAX_PAGES} pages while building ${ctx.studentLabel || 'this PDF'}. ` +
      'Something is placing content it cannot fit. Try a single week, or a single student.'
    );
  }
  ctx.doc.addPage();
  ctx.y = MARGIN;
}

// Called between rows and between attachments: a stall inside one student's
// drawing pass stops with their name attached rather than running forever.
function checkStudentBudget(ctx) {
  if (!ctx.studentStartedAt) return;
  const elapsed = Date.now() - ctx.studentStartedAt;
  if (elapsed > STUDENT_TIME_BUDGET_MS) {
    throw new Error(
      `Export stopped: ${ctx.studentLabel || 'one student'} took over ` +
      `${Math.round(STUDENT_TIME_BUDGET_MS / 1000)}s to lay out and was still going. ` +
      'Try a single week for this student.'
    );
  }
}

function addPageIfNeeded(ctx, required) {
  if (ctx.y + required > ctx.pageHeight - MARGIN) {
    addPage(ctx);
    return true;
  }
  return false;
}

function drawText(ctx, text, { x = MARGIN, size = FONT_BODY, bold = false, color = 0 } = {}) {
  ctx.doc.setFontSize(size);
  ctx.doc.setFont('helvetica', bold ? 'bold' : 'normal');
  ctx.doc.setTextColor(color);
  ctx.doc.text(text, x, ctx.y);
  ctx.doc.setTextColor(0);
}

// Wrapped paragraph that paginates line by line.
function drawParagraph(ctx, text, { x = MARGIN, size = FONT_BODY, color = 60, lineHeight = 5, width } = {}) {
  ctx.doc.setFontSize(size);
  ctx.doc.setFont('helvetica', 'normal');
  const maxWidth = width || ctx.contentWidth - (x - MARGIN);
  const lines = ctx.doc.splitTextToSize(String(text), maxWidth);
  lines.forEach(line => {
    addPageIfNeeded(ctx, lineHeight);
    ctx.doc.setTextColor(color);
    ctx.doc.text(line, x, ctx.y);
    ctx.doc.setTextColor(0);
    ctx.y += lineHeight;
  });
}

function drawHr(ctx, color = 220) {
  ctx.doc.setDrawColor(color);
  ctx.doc.line(MARGIN, ctx.y, ctx.pageWidth - MARGIN, ctx.y);
}

// ---------------------------------------------------------------------------
// Image loading and preparation.
//
// Every image is fetched with a timeout, decoded once, downscaled through a
// canvas, and re-encoded as JPEG before it reaches jsPDF. Embedding raw 4000px
// phone photos is what turned a 31-student export into a ten-minute main-thread
// stall. Anything that fails at any step resolves to null and is drawn as a
// named link, so one bad object never takes the export down with it.
// ---------------------------------------------------------------------------

// Yield to the event loop so the tab keeps painting and the progress label
// actually updates. Called between images as well as between students: one
// student with 104 photos used to hold the thread for eight minutes on their
// own, because the only yield was at the student boundary.
function yieldToMain() {
  return new Promise(resolve => setTimeout(resolve, 0));
}

// Where the time actually goes. fetchMs sums overlapping intervals (four are
// in flight at once), so it is not wall-clock; the ratio against processMs is
// the useful part, because it says whether a worker would buy anything.
function makeStats() {
  return { embedded: 0, skipped: 0, fetchMs: 0, processMs: 0 };
}

// Run worker over items with at most `limit` in flight. Results keep input
// order. A rejected worker is the worker's problem: prepareImage() never
// rejects, it resolves null.
async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let next = 0;

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await worker(items[index], index);
      // Hand the thread back between images, not just between students.
      await yieldToMain();
    }
  });

  await Promise.all(runners);
  return results;
}

async function fetchBlob(url) {
  // AbortController so a stalled object cannot hold the export open forever.
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => controller && controller.abort(), IMAGE_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, controller ? { signal: controller.signal } : undefined);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.blob();
  } finally {
    clearTimeout(timer);
  }
}

// Decode a blob to something drawable plus its natural size.
async function decodeBlob(blob) {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => bitmap.close && bitmap.close(),
    };
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not decode image'));
      el.src = objectUrl;
    });
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (err) {
    URL.revokeObjectURL(objectUrl);
    throw err;
  }
}

// Fetch, decode, downscale, re-encode. Resolves null on any failure.
async function prepareImage(url, stats) {
  let decoded = null;
  const startedAt = Date.now();
  let fetchedAt = startedAt;
  try {
    const blob = await fetchBlob(url);
    fetchedAt = Date.now();
    decoded = await decodeBlob(blob);

    const { width, height } = decoded;
    if (!width || !height) throw new Error('Image has no dimensions');

    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('No 2d canvas context');

    // JPEG has no alpha, so flatten onto white rather than onto black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.drawImage(decoded.source, 0, 0, targetWidth, targetHeight);

    const prepared = {
      dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
      width: targetWidth,
      height: targetHeight,
      format: 'JPEG',
    };

    if (stats) {
      const fetchMs = fetchedAt - startedAt;
      const processMs = Date.now() - fetchedAt;
      stats.embedded += 1;
      stats.fetchMs += fetchMs;
      stats.processMs += processMs;
      // Logged as it happens, not at the end: a stall used to produce no data
      // at all, because the summary only printed on completion.
      console.debug(
        `[submissionsPdf] image ${stats.embedded} ok  fetch ${fetchMs}ms  decode+encode ${processMs}ms  ${url.split('/').pop()}`
      );
    }
    return prepared;
  } catch (err) {
    if (stats) stats.skipped += 1;
    console.warn(`[submissionsPdf] image skipped: ${url}`, err && err.message ? err.message : err);
    return null;
  } finally {
    if (decoded && decoded.release) {
      try { decoded.release(); } catch (_) { /* nothing to clean up */ }
    }
  }
}

// Prefetch every image one student needs, bounded, de-duplicated by URL.
async function prepareImagesFor(rows, cache, stats) {
  const urls = [];
  (rows || []).forEach(row => {
    (row.files || []).forEach(file => {
      if (isImage(file) && file.file_url && !cache.has(file.file_url) && !urls.includes(file.file_url)) {
        urls.push(file.file_url);
      }
    });
  });

  if (urls.length === 0) return;

  const prepared = await mapWithConcurrency(urls, IMAGE_CONCURRENCY, url => prepareImage(url, stats));
  urls.forEach((url, i) => cache.set(url, prepared[i]));
}

// Draw one attachment. Synchronous: the bytes are already in the cache.
function drawAttachment(ctx, file, cache) {
  const name = displayFileName(file);
  const prepared = isImage(file) && file.file_url ? cache.get(file.file_url) : null;

  if (prepared) {
    // Fit to content width, then clamp to a single page height.
    const maxWidth = ctx.contentWidth;
    const maxHeight = ctx.pageHeight - MARGIN * 2 - 10;
    let drawWidth = maxWidth;
    let drawHeight = (prepared.height / prepared.width) * drawWidth;
    if (drawHeight > maxHeight) {
      drawHeight = maxHeight;
      drawWidth = (prepared.width / prepared.height) * drawHeight;
    }

    // A zero or NaN dimension would ask for a box that can never be placed.
    // Fall through to the link treatment rather than trying to draw it.
    if (!Number.isFinite(drawHeight) || !Number.isFinite(drawWidth) || drawHeight <= 0 || drawWidth <= 0) {
      console.warn(`[submissionsPdf] unusable image dimensions for ${name}, listing as link`);
    } else {
    addPageIfNeeded(ctx, drawHeight + 8);
    drawText(ctx, name, { size: FONT_SMALL, color: 120 });
    ctx.y += 4;
    ctx.doc.addImage(prepared.dataUrl, prepared.format, MARGIN, ctx.y, drawWidth, drawHeight);
    ctx.y += drawHeight + 6;
    return;
    }
  }

  // Everything else - Word docs, PDFs, HEIC, and images we could not fetch -
  // gets a named link so the reader can still find the file.
  addPageIfNeeded(ctx, 10);
  drawText(ctx, `File: ${name}`, { size: FONT_BODY, bold: true });
  ctx.y += 4.5;
  if (file.file_url) {
    ctx.doc.setFontSize(FONT_SMALL);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.doc.setTextColor(37, 99, 235);
    const lines = ctx.doc.splitTextToSize(file.file_url, ctx.contentWidth);
    lines.forEach((line, i) => {
      addPageIfNeeded(ctx, 4.5);
      // Only the first line carries the link annotation; the rest are the
      // wrapped remainder of the same URL, drawn as plain text.
      //
      // jsPDF's textWithLink() reads options.maxWidth unconditionally, so an
      // undefined options argument throws. An empty object would avoid the
      // throw but emit a link annotation with no url, so the continuation
      // lines go through doc.text() instead.
      if (i === 0) {
        ctx.doc.textWithLink(line, MARGIN, ctx.y, { url: file.file_url });
      } else {
        ctx.doc.text(line, MARGIN, ctx.y);
      }
      ctx.y += 4.5;
    });
    ctx.doc.setTextColor(0);
  }
  ctx.y += 2;
}

// ---------------------------------------------------------------------------
// One assignment block: heading, metadata line, submitted content.
// ---------------------------------------------------------------------------
async function drawRow(ctx, row, cache) {
  const { item, submission, files } = row;

  checkStudentBudget(ctx);
  addPageIfNeeded(ctx, 24);
  drawText(ctx, item.title || 'Untitled assignment', { size: FONT_H3, bold: true });
  ctx.y += 5.5;

  const metaBits = [];
  if (item.weekNumber) metaBits.push(`Week ${item.weekNumber}`);
  metaBits.push(statusLabel(submission));
  if (submission?.submitted_at) metaBits.push(fmtDateTime(submission.submitted_at));
  if (submission?.is_late) metaBits.push('LATE');
  if (submission?.grade != null) metaBits.push(`Grade: ${submission.grade}`);
  if (submission?.points_earned != null) {
    metaBits.push(`Points: ${submission.points_earned}${item.points ? ` / ${item.points}` : ''}`);
  }
  drawText(ctx, metaBits.join('  |  '), { size: FONT_SMALL, color: 120 });
  ctx.y += 6;

  if (!submission) {
    drawText(ctx, 'No submission on file.', { size: FONT_BODY, color: 150 });
    ctx.y += 8;
    return;
  }

  if (submission.text_content) {
    drawParagraph(ctx, submission.text_content, { color: 40, lineHeight: 5 });
    ctx.y += 3;
  }

  if (submission.link_url) {
    addPageIfNeeded(ctx, 8);
    ctx.doc.setFontSize(FONT_BODY);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.doc.setTextColor(37, 99, 235);
    ctx.doc.textWithLink(submission.link_url, MARGIN, ctx.y, { url: submission.link_url });
    ctx.doc.setTextColor(0);
    ctx.y += 7;
  }

  for (const file of files || []) {
    drawAttachment(ctx, file, cache);
    checkStudentBudget(ctx);
    // addImage() on a 1400px JPEG is not free either, so yield here too.
    await yieldToMain();
  }

  if (submission.feedback) {
    addPageIfNeeded(ctx, 12);
    drawText(ctx, 'Instructor feedback', { size: FONT_SMALL, bold: true, color: 90 });
    ctx.y += 4.5;
    drawParagraph(ctx, submission.feedback, { size: FONT_SMALL, color: 90, lineHeight: 4.5 });
  }

  if (!submission.text_content && !submission.link_url && (files || []).length === 0) {
    drawText(ctx, 'Submission has no attached content.', { size: FONT_BODY, color: 150 });
    ctx.y += 6;
  }

  ctx.y += 4;
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------
export async function generateSubmissionsPdf(entries, options = {}) {
  const {
    reportTitle = 'Assignment Submissions',
    generatedFor = '',
    fileName = 'Submissions.pdf',
    onProgress,
  } = options;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const ctx = makeContext(doc);

  // url -> prepared image (or null when it could not be embedded). Shared
  // across students so a file attached twice is only fetched once.
  const imageCache = new Map();
  const stats = makeStats();

  // Cover
  drawText(ctx, reportTitle, { size: FONT_H1, bold: true });
  ctx.y += 9;
  if (generatedFor) {
    drawText(ctx, generatedFor, { size: FONT_H2 });
    ctx.y += 7;
  }
  drawText(ctx, `Generated ${fmtToday()}`, { size: FONT_SMALL, color: 120 });
  ctx.y += 5;
  drawText(ctx, `${entries.length} student${entries.length === 1 ? '' : 's'}`, {
    size: FONT_SMALL,
    color: 120,
  });
  ctx.y += 6;
  drawHr(ctx);

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const label = entry.student?.name || `student ${i + 1}`;
    const imageCount = (entry.rows || []).reduce(
      (n, row) => n + (row.files || []).filter(f => isImage(f) && f.file_url).length,
      0
    );

    if (onProgress) onProgress(i + 1, entries.length, entry.student?.name);

    // One line per student as it starts, so a stall shows a "start" with no
    // matching "done" and names who it stopped on.
    const studentStartedAt = Date.now();
    console.info(
      `[submissionsPdf] student ${i + 1}/${entries.length} start: ${label} ` +
      `(${(entry.rows || []).length} items, ${imageCount} images)`
    );

    ctx.studentLabel = label;
    ctx.studentStartedAt = studentStartedAt;

    // Let React paint the progress label before this student's work starts.
    await yieldToMain();

    // Fetch and downscale this student's images up front, a few at a time,
    // so the drawing pass below never blocks on the network.
    await prepareImagesFor(entry.rows, imageCache, stats);

    addPage(ctx);

    drawText(ctx, entry.student?.name || 'Unknown student', { size: FONT_H2, bold: true });
    ctx.y += 6;
    if (entry.student?.email) {
      drawText(ctx, entry.student.email, { size: FONT_SMALL, color: 120 });
      ctx.y += 5;
    }
    drawHr(ctx);
    ctx.y += 7;

    if (!entry.rows || entry.rows.length === 0) {
      drawText(ctx, 'No assignments in this selection.', { size: FONT_BODY, color: 150 });
      console.info(`[submissionsPdf] student ${i + 1}/${entries.length} done:  ${label} (nothing in scope)`);
      continue;
    }

    for (const row of entry.rows) {
      await drawRow(ctx, row, imageCache);
    }

    console.info(
      `[submissionsPdf] student ${i + 1}/${entries.length} done:  ${label} ` +
      `in ${((Date.now() - studentStartedAt) / 1000).toFixed(1)}s, ${ctx.pages} pages so far`
    );
  }

  if (stats.skipped > 0) {
    console.warn(`${stats.skipped} image attachment(s) could not be embedded and are listed as links.`);
  }
  if (stats.embedded > 0) {
    // Read this after a real run to decide whether moving decode and re-encode
    // to a worker is worth it: if processMs is small next to fetchMs, it is not.
    console.info(
      `[submissionsPdf] ${stats.embedded} images embedded, ${stats.skipped} skipped. ` +
      `fetch ${(stats.fetchMs / 1000).toFixed(1)}s (avg ${Math.round(stats.fetchMs / stats.embedded)}ms/image, overlapped ${IMAGE_CONCURRENCY}x), ` +
      `decode+encode ${(stats.processMs / 1000).toFixed(1)}s (avg ${Math.round(stats.processMs / stats.embedded)}ms/image, main thread).`
    );
  }

  doc.save(fileName);
  return {
    students: entries.length,
    imagesEmbedded: stats.embedded,
    imagesSkipped: stats.skipped,
    fetchMs: stats.fetchMs,
    processMs: stats.processMs
  };
}

export default generateSubmissionsPdf;
